/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconClose from '@assets/icons/close.svg?react';
import IconSave from '@assets/icons/save.svg?react';
import {
 Box,
 Button,
 FormControl,
 FormHelperText,
 IconButton,
 Popover,
 TextField,
 useTheme,
} from '@mui/material';
import { SetStateAction, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
 getConfigStorageItem,
 setConfigStorageItem,
} from '../../../hooks/useConfigStorage';

interface TranslationFormValues {
 translation: string;
}

const getTranslationFromStorage = (
 tableName: string,
 colName: string,
): string => {
 try {
  const translations = getConfigStorageItem('translations') || {};
  return translations[tableName]?.[colName] || '';
 } catch {
  return '';
 }
};

const saveTranslationToStorage = (
 tableName: string,
 colName: string,
 translation: string,
): void => {
 try {
  const translations = getConfigStorageItem('translations') || {};
  const tableTranslations = translations[tableName] || {};
  const updatedTranslations = { ...tableTranslations };
  if (translation.trim() === '') {
   delete updatedTranslations[colName];
  } else {
   updatedTranslations[colName] = translation;
  }
  setConfigStorageItem({
   page: 'translations',
   key: tableName,
   value: updatedTranslations,
  });
 } catch (error) {
  console.error('Failed to save translation to localStorage', error);
 }
};

const TranslationModal = ({
 data,
 setOpen,
 tableName,
}: {
 data: {
  anchorElement: HTMLDivElement | null;
  colName: string;
 };
 setOpen: SetStateAction<any>;
 tableName: string;
}) => {
 const th = useTheme();
 const { t } = useTranslation();

 const { colName, anchorElement } = data;

 const {
  handleSubmit,
  control,
  formState: { errors },
  reset,
 } = useForm<TranslationFormValues>({
  mode: 'onChange',
  defaultValues: { translation: '' },
 });

 const handleSave = (formData: TranslationFormValues) => {
  saveTranslationToStorage(tableName, colName, formData.translation);
  setOpen(false);
  window.location.reload();
 };

 const handleClear = () => {
  saveTranslationToStorage(tableName, colName, '');
  setOpen(false);
  window.location.reload();
 };

 useEffect(() => {
  const savedTranslation = getTranslationFromStorage(tableName, colName);
  reset({ translation: savedTranslation });
 }, [tableName, colName, reset]);

 return (
  <Popover
   id="translation-modal"
   open={!!anchorElement}
   anchorEl={anchorElement}
   onClose={() => setOpen(false)}
   anchorOrigin={{
    vertical: 'bottom',
    horizontal: 'left',
   }}
  >
   <Box
    component="form"
    onSubmit={handleSubmit(handleSave)}
    noValidate
    sx={{
     minWidth: '300px',
     borderRadius: '4px',
     border: `1px solid ${th.palette.kemek.lightestGray}`,
     backgroundColor: th.palette.kemek.lightBlue,
     p: '16px',
     display: 'flex',
     flexDirection: 'column',
     gap: '12px',
    }}
   >
    <Controller
     name="translation"
     control={control}
     render={({ field }) => (
      <FormControl variant="filled" error={!!errors.translation}>
       <TextField
        size="small"
        slotProps={{
         input: {
          autoComplete: 'off',
          disableUnderline: true,
         },
        }}
        {...field}
        error={!!errors.translation}
        helperText={errors.translation?.message}
        variant="filled"
        id="translation-input"
        label={t('global.translation')}
        sx={{
         '& .MuiFilledInput-input': {
          height: '48px',
          boxSizing: 'border-box',
          background: 'transparent',
         },
        }}
       />
       {errors.translation && (
        <FormHelperText
         sx={{
          whiteSpace: 'nowrap',
          fontSize: 9,
          m: 0,
         }}
        >
         {errors.translation.message}
        </FormHelperText>
       )}
      </FormControl>
     )}
    />
    <Box
     sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
     }}
    >
     <Button
      type="button"
      aria-label={t('global.cancel') || 'Atšaukti'}
      variant="text"
      sx={{ color: th.palette.kemek.darkGray }}
      onClick={() => setOpen(false)}
     >
      {t('global.cancel') || 'Atšaukti'}
     </Button>
     <IconButton
      type="button"
      onClick={handleClear}
      aria-label="clear translation"
     >
      <IconClose style={{ color: th.palette.kemek.red }} />
     </IconButton>
     <IconButton type="submit" aria-label="save translation">
      <IconSave style={{ color: th.palette.kemek.blue }} />
     </IconButton>
    </Box>
   </Box>
  </Popover>
 );
};

export default TranslationModal;
